// Composer Studio — 드래그 오버레이 레이어 (Phase 3b-2)
//
// 엔진(RenderTemplate) DOM은 무수정. 대신 캔버스 위에 절대배치 오버레이를 띄워:
//   - 각 드래그 가능 노드(body/group.children 원소) 외곽 테두리에 이동-커서 드래그 링(@dnd-kit useDraggable)
//     → 보이는 핸들 없이 노드 테두리 hover 시 cursor:move, 거기서 드래그 시작(중앙은 클릭 통과)
//   - 형제 사이/그룹 내부에 삽입 드롭존(@dnd-kit useDroppable) + 삽입선 인디케이터
// 를 렌더한다. 좌표는 렌더된 [data-composer-node-id] 엘리먼트의 getBoundingClientRect를
// 캔버스 기준 상대좌표로 환산해 구한다.
//
// 표 셀 내부 content 노드 / repeat.template 내부는 이번 범위 외(listDraggables/listContainers가 제외).

import { useDraggable, useDroppable } from '@dnd-kit/core';
import type { ComposerNode } from '@safetyhub/report-composer';
import type { Rect } from './canvasRect';
import { getNodeRect } from './canvasRect';
import type { ContainerEntry } from './nodeTreeWalk';
import { listContainers } from './nodeTreeWalk';

/** 드롭 타깃 식별자 — droppable id 인코딩 */
export interface DropSlot {
  /** 삽입될 부모 group id (body면 null) */
  parentId: string | null;
  /** 삽입 index (해당 컨테이너 children 기준) */
  index: number;
}

/** droppable id 직렬화 (parentId가 null이면 '__body__') */
export function encodeDropId(parentId: string | null, index: number): string {
  return `drop::${parentId ?? '__body__'}::${index}`;
}

export function decodeDropId(id: string): DropSlot | null {
  const m = /^drop::(.+)::(\d+)$/.exec(id);
  if (!m) return null;
  return { parentId: m[1] === '__body__' ? null : m[1], index: Number(m[2]) };
}

/** 외곽 드래그 링 두께(px) — 노드 테두리 근처에서 드래그를 캡처할 영역. */
const RING = 7;

/**
 * 외곽 이동-커서 드래그 영역 — 보이는 핸들(⠿) 대신, 노드 테두리(외곽 링)에 hover 시
 * `cursor: move`로 바뀌고 거기서 드래그를 시작한다. 내부(콘텐츠) 영역은 pointer-events를
 * 통과시켜 안쪽 클릭=선택/텍스트 편집을 그대로 유지한다.
 *
 * 한 노드당 useDraggable 1개. setNodeRef는 프레임에 붙이고, 드래그 리스너는 4개의
 * 테두리 스트립(top/right/bottom/left)에 동일하게 spread한다(중앙은 pointer-events:none).
 * 프레임은 노드 경계 그대로(외곽 확장 없음)라 이웃 노드 스트립이 서로를 덮지 않는다.
 * 얇은 노드(line/spacer)는 스트립 두께를 노드 크기 절반 이내로 clamp해 자기 영역에 맞춘다.
 */
function NodeDragRing({
  node,
  rect,
  onSelect,
}: {
  node: ComposerNode;
  rect: Rect;
  onSelect: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: node.id });

  // 드래그 중에는 grabbing, 평소엔 move 커서.
  const cursor = isDragging ? 'grabbing' : 'move';

  // 테두리 스트립 공통 props(드래그 리스너 + 클릭 전파 차단 + 커서).
  const stripProps = {
    ...attributes,
    ...listeners,
    // 얇은 노드(line/spacer 등)는 클릭 지점 대부분이 스트립이라, 단순 클릭(activation
    // distance 미만 움직임)이 캔버스 선택 위임에 닿지 못한다. 그래서 스트립 클릭 시
    // 직접 해당 노드를 선택한다. stopPropagation으로 캔버스 핸들러 이중처리는 막는다.
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(node.id);
    },
    style: { position: 'absolute' as const, cursor, touchAction: 'none' as const },
  };

  // 링 프레임은 노드 경계 그대로(외곽 확장 없음) → 이웃 노드 스트립이 서로를 덮지 않아
  // 얇은 노드(header-rect 등)도 자기 스트립이 잡혀 자기 자신이 선택된다.
  const frameTop = rect.top;
  const frameLeft = rect.left;
  const frameW = rect.width;
  const frameH = rect.height;

  // 얇은 노드 대응 — 상/하 스트립 height와 좌/우 스트립 width를 노드 크기 절반 이내로 clamp해
  // 스트립이 자기 영역 안에 들어오게 한다(8px 노드는 ringV=4 → 상4+하4=8). 중앙이 좁아도
  // 스트립 onClick 선택으로 커버된다.
  const ringV = Math.min(RING, Math.max(1, Math.floor(rect.height / 2))); // 상/하 스트립 height
  const ringH = Math.min(RING, Math.max(1, Math.floor(rect.width / 2)));  // 좌/우 스트립 width

  return (
    <div
      ref={setNodeRef}
      aria-label={`${node.id} 드래그 이동`}
      style={{
        position: 'absolute',
        top: frameTop,
        left: frameLeft,
        width: frameW,
        height: frameH,
        // 프레임 자체는 이벤트 통과(스트립만 캡처) + 드래그 중 윤곽선 옅게 강조.
        pointerEvents: 'none',
        outline: isDragging ? '1px dashed rgba(37,99,235,0.6)' : undefined,
        outlineOffset: 0,
        opacity: isDragging ? 0.6 : 1,
        zIndex: 25,
      }}
    >
      {/* 상단 스트립 */}
      <div
        {...stripProps}
        style={{ ...stripProps.style, top: 0, left: 0, right: 0, height: ringV, pointerEvents: 'auto' }}
      />
      {/* 하단 스트립 */}
      <div
        {...stripProps}
        style={{ ...stripProps.style, bottom: 0, left: 0, right: 0, height: ringV, pointerEvents: 'auto' }}
      />
      {/* 좌측 스트립 */}
      <div
        {...stripProps}
        style={{ ...stripProps.style, top: 0, bottom: 0, left: 0, width: ringH, pointerEvents: 'auto' }}
      />
      {/* 우측 스트립 */}
      <div
        {...stripProps}
        style={{ ...stripProps.style, top: 0, bottom: 0, right: 0, width: ringH, pointerEvents: 'auto' }}
      />
      {/* 중앙은 비움(pointer-events 통과) → 안쪽 클릭=선택/편집 유지 */}
    </div>
  );
}

/**
 * 삽입 드롭존 — 컨테이너 children의 각 "사이"(index 0..n)에 띄운다.
 * 활성 드롭존이면 삽입선을 강조한다.
 */
function InsertDropZone({
  parentId,
  index,
  rect,
  horizontal,
}: {
  parentId: string | null;
  index: number;
  rect: Rect;
  horizontal: boolean;
}) {
  const id = encodeDropId(parentId, index);
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        position: 'absolute',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        zIndex: 20,
        pointerEvents: 'auto',
      }}
    >
      {/* 삽입선 인디케이터 */}
      <div
        style={
          horizontal
            ? {
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                width: isOver ? 3 : 0,
                marginLeft: -1.5,
                background: '#4f46e5', // brand-600
                borderRadius: 2,
              }
            : {
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                height: isOver ? 3 : 0,
                marginTop: -1.5,
                background: '#4f46e5', // brand-600
                borderRadius: 2,
              }
        }
      />
    </div>
  );
}

/**
 * 한 컨테이너(body 또는 group)에 대한 삽입 드롭존들을 계산해 렌더.
 * children 사이 + 양 끝(index 0 .. children.length)에 드롭존을 만든다.
 * 자식 rect를 읽어 "사이" 영역을 결정한다(세로 컬럼 가정 — group.direction='row'면 가로 삽입선).
 */
function ContainerDropZones({
  canvas,
  container,
  isRow,
}: {
  canvas: HTMLElement;
  container: ContainerEntry;
  isRow: boolean;
}) {
  const { parentId, children } = container;

  // 빈 컨테이너 → 컨테이너(group) 자체 rect 전체를 index 0 드롭존으로.
  // (body가 비는 경우는 거의 없고, group id가 있어야 rect를 잡을 수 있다.)
  if (children.length === 0) {
    if (!parentId) return null;
    const rect = getNodeRect(canvas, parentId);
    if (!rect || (rect.width === 0 && rect.height === 0)) return null;
    return <InsertDropZone parentId={parentId} index={0} rect={rect} horizontal={isRow} />;
  }

  // 각 자식 rect
  const childRects = children
    .map((c) => ({ id: c.id, rect: getNodeRect(canvas, c.id) }))
    .filter((x): x is { id: string; rect: Rect } => x.rect !== null);
  if (childRects.length === 0) return null;

  const zones: React.ReactNode[] = [];
  const ZONE = 14; // 삽입존 두께(px)

  childRects.forEach((cr, i) => {
    // i번째 자식의 "앞" 드롭존 (index = i)
    if (isRow) {
      zones.push(
        <InsertDropZone
          key={`pre-${i}`}
          parentId={parentId}
          index={i}
          rect={{ top: cr.rect.top, left: cr.rect.left - ZONE / 2, width: ZONE, height: cr.rect.height }}
          horizontal
        />,
      );
    } else {
      zones.push(
        <InsertDropZone
          key={`pre-${i}`}
          parentId={parentId}
          index={i}
          rect={{ top: cr.rect.top - ZONE / 2, left: cr.rect.left, width: cr.rect.width, height: ZONE }}
          horizontal={false}
        />,
      );
    }
  });

  // 마지막 자식 "뒤" 드롭존 (index = length)
  const last = childRects[childRects.length - 1].rect;
  if (isRow) {
    zones.push(
      <InsertDropZone
        key="post"
        parentId={parentId}
        index={children.length}
        rect={{ top: last.top, left: last.left + last.width - ZONE / 2, width: ZONE, height: last.height }}
        horizontal
      />,
    );
  } else {
    zones.push(
      <InsertDropZone
        key="post"
        parentId={parentId}
        index={children.length}
        rect={{ top: last.top + last.height - ZONE / 2, left: last.left, width: last.width, height: ZONE }}
        horizontal={false}
      />,
    );
  }

  return <>{zones}</>;
}

/**
 * 드래그 오버레이 — canvas DOM이 준비된 뒤(렌더 후) 마운트한다.
 * isDragging일 때만 드롭존을 보이고, 평소엔 외곽 이동-커서 드래그 링만 표시한다.
 */
export function DragLayer({
  canvas,
  body,
  isDragging,
  groupDirectionOf,
  onSelect,
}: {
  canvas: HTMLElement | null;
  body: ComposerNode[];
  isDragging: boolean;
  /** group id → direction('row'|'column') 조회 (없으면 column) */
  groupDirectionOf: (groupId: string) => 'row' | 'column';
  /** 링 스트립 클릭 시 해당 노드 선택 */
  onSelect: (id: string) => void;
}) {
  if (!canvas) return null;

  const containers = listContainers(body);

  return (
    <div
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      aria-hidden={false}
    >
      {/* 외곽 이동-커서 드래그 링 (항상 표시 — 테두리 스트립만 pointerEvents 활성) */}
      <div style={{ pointerEvents: 'none' }}>
        {flattenHandles(body).map((entry) => {
          const rect = getNodeRect(canvas, entry.id);
          if (!rect) return null;
          return (
            <NodeDragRing
              key={`ring-${entry.id}`}
              node={entry.node}
              rect={rect}
              onSelect={onSelect}
            />
          );
        })}
      </div>

      {/* 드롭존 (드래그 중에만) */}
      {isDragging && (
        <div style={{ pointerEvents: 'none' }}>
          {containers.map((c) => (
            <div key={`c-${c.parentId ?? '__body__'}`} style={{ pointerEvents: 'auto' }}>
              <ContainerDropZones
                canvas={canvas}
                container={c}
                isRow={c.parentId ? groupDirectionOf(c.parentId) === 'row' : false}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 핸들 표시 대상(드래그 가능 노드) 평탄화.
 * 하강 정책: group/stack.children만(table cell.content · repeat.template 내부는 드래그 범위 외 — 제외).
 * listDraggables/listContainers와 동일 범위라 walkNodeTree(includeCell:false, includeRepeat:false)로도
 * 표현 가능하나, 드래그 전용 최소 로직이므로 회귀 0 우선해 현행 손코딩 walk를 유지한다.
 */
function flattenHandles(body: ComposerNode[]): { id: string; node: ComposerNode }[] {
  const out: { id: string; node: ComposerNode }[] = [];
  const walk = (nodes: ComposerNode[]) => {
    for (const n of nodes) {
      out.push({ id: n.id, node: n });
      if (n.kind === 'group' || n.kind === 'stack') walk(n.children);
    }
  };
  walk(body);
  return out;
}
